'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { APPLIANCES } from '../data';
import {
  ApplianceId,
  batteryCapacityEu,
  clockLabel,
  drainEuPerHour,
  GAME_START_HOUR_OF_DAY,
  GameState,
  RESTORE_MINUTE,
  ShopItemId,
  solarGenEuPerHour,
} from '../engine';

import { APPLIANCE_ICONS, IconThermometer, moodIcon } from './icons';

type Props = {
  onRestart: () => void;
  onSetSpeed: (speed: 1 | 2) => void;
  onToggleLoad: (applianceId: ApplianceId) => void;
  state: GameState;
};

const TOGGLEABLE = Object.values(APPLIANCES).filter(({ essential, id }) => !essential && id !== ApplianceId.LIGHTS);
const ESSENTIALS = Object.values(APPLIANCES).filter(({ essential }) => essential);

const ESSENTIAL_WARNINGS: Partial<Record<ApplianceId, string>> = {
  [ApplianceId.FRIDGE]: 'off too long = spoiled food',
  [ApplianceId.WIFI]: 'the teen will notice',
};

const ZapRating = ({ drainPerHour }: { drainPerHour: number }) => (
  <x.span color="uiHelperCopy" display="inline-flex">
    {Array.from({ length: Math.min(4, Math.max(1, Math.ceil(drainPerHour))) }, (_, i) => (
      <DS.IconLightningFill key={i} size={3} />
    ))}
  </x.span>
);

const ApplianceRow = ({
  applianceId,
  isOn,
  onToggleLoad,
  warning,
}: {
  applianceId: ApplianceId;
  isOn: boolean;
  onToggleLoad: (applianceId: ApplianceId) => void;
  warning?: string;
}) => {
  const { drainPerHour, name } = APPLIANCES[applianceId];
  const Icon = APPLIANCE_ICONS[applianceId];
  return (
    <x.div alignItems="center" display="flex" gap={2} justifyContent="space-between">
      <x.div>
        <DS.Text alignItems="center" as="span" display="inline-flex" gap={1} text="body14">
          <Icon size={4} />
          <span>{name}</span>
        </DS.Text>
        {warning !== undefined && (
          <DS.Text color={isOn ? 'uiHelperCopy' : 'uiError'} text="body12">
            {warning}
          </DS.Text>
        )}
      </x.div>
      <x.div alignItems="center" display="flex" gap={1}>
        <ZapRating drainPerHour={drainPerHour} />
        <DS.Toggle
          handleToggle={() => onToggleLoad(applianceId)}
          size="xs"
          toggled={isOn}
          toggledStateOffHint={`Turn on ${name}`}
          toggledStateOnHint={`Turn off ${name}`}
        />
      </x.div>
    </x.div>
  );
};

export const PhonePanel = ({ onRestart, onSetSpeed, onToggleLoad, state }: Props) => {
  const capacity = batteryCapacityEu(state.prep);
  const batteryPct = capacity > 0 ? Math.round((state.batteryEu / capacity) * 100) : 0;
  const solarGen = solarGenEuPerHour(state);
  const drain = drainEuPerHour(state);
  const dayTwo = state.gameMinutes >= (24 - GAME_START_HOUR_OF_DAY) * 60;
  const overtime = state.endless && state.gameMinutes > RESTORE_MINUTE;
  const isOn = (id: ApplianceId) => state.activeLoads.some(({ applianceId }) => applianceId === id);
  const MoodIcon = moodIcon(state.mood);
  const ownsSolar = Boolean(
    state.prep.purchases[ShopItemId.SOLAR_LARGE] || state.prep.purchases[ShopItemId.SOLAR_SMALL]
  );

  return (
    <DS.Card density="dense">
      <DS.Flex gap={3}>
        <x.div alignItems="center" display="flex" flexWrap="wrap" gap={2} justifyContent="space-between">
          <x.b>{`${clockLabel(state.gameMinutes)} · ${overtime ? 'overtime' : dayTwo ? 'day 2' : 'day 1'}`}</x.b>
          <x.span alignItems="center" display="inline-flex" gap={2}>
            <DS.ButtonGroup size="xs" variant="tertiary">
              <DS.Button onClick={() => onSetSpeed(1)} pressed={state.speed === 1}>
                1x
              </DS.Button>
              <DS.Button onClick={() => onSetSpeed(2)} pressed={state.speed === 2}>
                2x
              </DS.Button>
            </DS.ButtonGroup>
            <DS.Button onClick={onRestart} size="xs" variant="text">
              Start over
            </DS.Button>
          </x.span>
        </x.div>

        <x.div>
          <x.div alignItems="baseline" display="flex" justifyContent="space-between">
            <x.span alignItems="center" display="inline-flex" gap={1}>
              <DS.IconBatteryLightningVertical size={4} />
              <span>{capacity > 0 ? `Battery ${batteryPct}%` : 'No battery installed'}</span>
            </x.span>
            <x.span alignItems="center" color="uiHelperCopy" display="inline-flex" gap={1}>
              <DS.IconSun size={3} />
              <DS.Text text="body12">{`+${solarGen.toFixed(1)}`}</DS.Text>
              <DS.IconPlug size={3} />
              <DS.Text text="body12">{`−${state.gridUp ? '0.0' : drain.toFixed(1)}`}</DS.Text>
            </x.span>
          </x.div>
          <x.div bg="uiBgAlternate" borderRadius={8} h="12px" mt={1} overflow="hidden">
            <x.div
              bg={batteryPct > 30 ? 'uiSuccess' : 'uiError'}
              h="100%"
              transition="width 0.4s"
              w={`${batteryPct}%`}
            />
          </x.div>
        </x.div>

        <x.div display="flex" gap={4} justifyContent="space-between">
          <x.span alignItems="center" display="inline-flex" gap={1}>
            <IconThermometer size={4} />
            <span>{`${Math.round(state.indoorTempF)}°F inside`}</span>
          </x.span>
          <x.span alignItems="center" display="inline-flex" gap={1}>
            <MoodIcon size={4} />
            <span>family mood</span>
          </x.span>
        </x.div>

        {state.dark && (
          <x.div
            bg="uiCard.errorBg"
            borderColor="uiCard.errorBorderColor"
            borderRadius={8}
            borderStyle="solid"
            borderWidth="0 0 0 3px"
            minW={0}
            p={3}
            role="alert"
          >
            <DS.Text fontWeight="bold" text="body14">
              {ownsSolar
                ? 'Battery empty — the house is unpowered until the panels recharge it.'
                : 'Battery empty — the house is unpowered until the grid returns.'}
            </DS.Text>
          </x.div>
        )}

        <DS.Divider fullBleed />

        <x.div>
          <DS.Text color="uiHelperCopy" text="body12">
            Essentials
          </DS.Text>
          <x.div display="grid" gap={2} gridTemplateColumns={{ _: '1fr 1fr', md: '1fr' }} mt={1}>
            {ESSENTIALS.map(({ id }) => (
              <ApplianceRow
                applianceId={id}
                isOn={isOn(id)}
                key={id}
                onToggleLoad={onToggleLoad}
                warning={ESSENTIAL_WARNINGS[id]}
              />
            ))}
          </x.div>
          <x.div alignItems="center" color="uiHelperCopy" display="flex" gap={1} mt={1}>
            <DS.IconLightbulb size={3} />
            <DS.Text text="body12">Lights</DS.Text>
            <ZapRating drainPerHour={APPLIANCES[ApplianceId.LIGHTS].drainPerHour} />
            <DS.Text text="body12">run automatically at night</DS.Text>
          </x.div>
        </x.div>

        <x.div display="grid" gap={2} gridTemplateColumns={{ _: '1fr 1fr', md: '1fr' }}>
          {TOGGLEABLE.map(({ id }) => (
            <ApplianceRow applianceId={id} isOn={isOn(id)} key={id} onToggleLoad={onToggleLoad} />
          ))}
        </x.div>
      </DS.Flex>
    </DS.Card>
  );
};
